<template>
  <app-layout class="flex-container extra-gap">
    <app-layout-header class="flex-container">
      <AppButton @click="close()" muted no-shadow>
        <AppImage src="images/icons/close.svg" />
      </AppButton>

      <h2 class="emphasis">Alterar meu telefone</h2>
    </app-layout-header>

    <app-layout-content>
      <AppForm id="form" @submit="checkNewPhoneNumber">
        <label>
          <span>Insira o seu número de telefone atual:</span>

          <AppInput
            v-model="newPhoneNumber"
            type="tel"
            ref="newPhoneNumber"
            pattern="\(\d{2}\)\s(9\s)?\d{4}-\d{4}"
            inputmode="numeric"
            autofocus
          />
        </label>
      </AppForm>
    </app-layout-content>

    <app-layout-footer>
      <AppButton
        type="submit"
        form="form"
        primary
        :loading="isChangingPhoneNumber"
        :disabled="isChangingPhoneNumber"
        fullWidth
        >Alterar</AppButton
      >
    </app-layout-footer>
  </app-layout>
</template>

<script>
import AppButton from '@/components/App/Buttons/Button.vue'
import AppImage from '@/components/App/Image.vue'
import AppForm from '@/components/App/Form.vue'
import AppInput from '@/components/App/Input.vue'

import {
  getFormattedPhoneNumber,
  sanitizePhoneNumber,
  validatePhoneNumber,
  getSupportWhatsAppURL,
} from '@/helpers'

export default {
  name: 'UserChangePhoneNumber',

  components: {
    AppButton,
    AppImage,
    AppForm,
    AppInput,
  },

  data() {
    return {
      isChangingPhoneNumber: false,
      newPhoneNumber: undefined,
    }
  },

  computed: {
    currentPhoneNumber() {
      const user = this.$attrs.user ?? app.user

      return user?.phoneNumber?.value ?? user?.phoneNumber
    },
  },

  created() {
    this.newPhoneNumber = this.currentPhoneNumber
  },

  methods: {
    close() {
      this.$emit('close')
    },

    async checkNewPhoneNumber(form) {
      const { newPhoneNumber } = this
      const { newPhoneNumber: newPhoneNumberInput } = this.$refs

      try {
        form.disable = true
        this.isChangingPhoneNumber = true

        const { currentPhoneNumber } = this
        const sanitizedNewPhoneNumber = sanitizePhoneNumber(newPhoneNumber)

        if (currentPhoneNumber === sanitizedNewPhoneNumber)
          return await app.alert({
            title: 'Opss...',
            content: `Utilize um numero de telefone diferente para alterar!`,
            onClose: newPhoneNumberInput.select,
          })

        const phoneNumberValidationResult = validatePhoneNumber(newPhoneNumber)

        if (phoneNumberValidationResult !== true)
          throw {
            code: phoneNumberValidationResult,
          }

        const updatedUser = await app.callApi({
          method: 'PATCH',
          version: 1,
          action: 'me',
          data: {
            phoneNumber: sanitizedNewPhoneNumber,
          },
        })

        app.user = updatedUser

        this.$emit('changed')
      } catch (error) {
        switch (error?.code) {
          case 'EMPTY_PHONE_NUMBER':
            await app.alert({
              title: 'Opss...',
              content: `Digite o seu número de telefone!`,
              onClose: newPhoneNumberInput.focus,
            })
            break

          case 'IS_NOT_MOBILE_NUMBER':
            await app.alert({
              title: 'Opss...',
              content: `O número precisa ser de um celular!`,
              onClose: newPhoneNumberInput.select,
            })
            break

          case 'NINTH_DIGIT_NOT_SET':
            await app.alert({
              title: 'Opss...',
              content: `Acho que você esqueceu de informar o nono dígito do seu celular...`,
              onClose: newPhoneNumberInput.select,
            })
            break

          case 'INVALID_PHONE_NUMBER':
            await app.alert({
              title: 'Opss...',
              content: `O número de telefone parece inválido 😕`,
              onClose: newPhoneNumberInput.select,
            })
            break

          case 'PHONE_NUMBER_IN_USE':
            const formattedNewPhoneNumber =
              getFormattedPhoneNumber(newPhoneNumber)
            const cityId = app.lastVisitedCityId

            let whatsAppUrl = getSupportWhatsAppURL(cityId)

            await app.dialog({
              title: 'Opss...',
              content: `O número <b>${formattedNewPhoneNumber}</b> já está sendo utilizado pelo usuário <b>${error.maskedEmail}</b>! 🙁`,
              buttons: [
                {
                  label: 'Ok',
                  primary: true,
                },
                {
                  label: 'Falar com o suporte',
                  onClick: async modal => {
                    whatsAppUrl = await whatsAppUrl

                    app.openInExternalBrowser(whatsAppUrl)
                    modal.close()
                  },
                },
              ],
            })
            break

          default:
            console.error(error)

            await app.alert({
              title: 'Opss...',
              content: `Ocorreu um erro desconhecido! Por favor, verifique a sua conexão e tente novamente.`,
            })
            break
        }
      } finally {
        this.isChangingPhoneNumber = false
        form.disable = false
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
